import React, { useState, useEffect, useCallback } from "react";
import { Mic, StopCircle, Loader2 } from "lucide-react";
import { ImSpinner2 } from "react-icons/im";

let recognition = null;
if (typeof window !== "undefined" && "webkitSpeechRecognition" in window) {
  const SpeechRecognition = window.webkitSpeechRecognition;
  recognition = new SpeechRecognition();
  recognition.continuous = true;
  recognition.interimResults = true;
  recognition.lang = "en-US";
}

const CVoiceRecorder = ({
  load,
  setLoad,
  handleTranscript,
  isRecording,
  setIsRecording,
}) => {
  const [isInitializing, setIsInitializing] = useState(false);
  const [transcript, setTranscript] = useState(""); // State for ongoing speech
  const [finalTranscript, setFinalTranscript] = useState(""); // State for final result

  useEffect(() => {
    return () => {
      if (recognition && isRecording) {
        recognition.stop();
      }
    };
  }, [isRecording]);

  useEffect(() => {
    if (!recognition) return;

    const handleResult = (event) => {
      let speechToText = Array.from(event.results)
        .map((result) => result[0].transcript)
        .join(" ");

      if (event.results[event.results.length - 1].isFinal) {
        // If the result is final, update finalTranscript
        setFinalTranscript((prevTranscript) => prevTranscript + speechToText);
      } else {
        // If it's interim, update transcript
        setTranscript(speechToText);
      }

      // Optionally, update the parent component with interim or final transcript
      handleTranscript(transcript);
    };

    const handleError = (event) => {
      console.error("Speech recognition error:", event.error);
      setLoad(false);
      setIsRecording(false);
      setIsInitializing(false);
    };

    const handleEnd = () => {
      setLoad(false);
      setIsRecording(false);
      setIsInitializing(false);
    };

    const handleStart = () => {
      setLoad(true);
      setTimeout(() => {
        setIsInitializing(false);
      }, 1000);
    };

    recognition.onresult = handleResult;
    recognition.onerror = handleError;
    recognition.onend = handleEnd;
    recognition.onstart = handleStart;

    return () => {
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      recognition.onstart = null;
    };
  }, [setLoad, handleTranscript]);

  const handleRecording = useCallback(() => {
    if (!recognition) {
      console.warn("Speech recognition is not supported in this browser.");
      return;
    }

    if (isRecording) {
      setLoad(false);
      setIsRecording(false);
      recognition.stop();
    } else {
      setIsInitializing(true);
      setIsRecording(true);

      try {
        recognition.start();
        // Start recording immediately
      } catch (error) {
        console.error("Failed to start recording:", error);
        setLoad(false);
        setIsRecording(false);
        setIsInitializing(false);
      }
    }
  }, [isRecording, setLoad]);

  return (
    <>
      {isInitializing && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 z-50">
          <div className="bg-white p-4 flex gap-2 rounded-md shadow-lg">
            <ImSpinner2 size={24} className="animate-spin" />
            <p>Loading of Resources... Please wait</p>
          </div>
        </div>
      )}
      <button
        id="chat-custom-recording"
        onClick={handleRecording}
        className="p-2 rounded-full hover:bg-gray-100 transition-colors"
        aria-label={isRecording ? "Stop recording" : "Start recording"}
        disabled={isInitializing}>
        {isInitializing ? (
          <Loader2 className="w-6 h-6 animate-spin" />
        ) : load ? (
          <StopCircle className="w-6 h-6 text-red-600" />
        ) : (
          <Mic className="w-6 h-6" />
        )}
      </button>
    </>
  );
};

export default CVoiceRecorder;
